/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import {
    fork, call, takeEvery, put, select,
} from 'redux-saga/effects';
import mbpLogger from 'mbp-logger';
import memberDucks from '../../../../../Member/ducks';
import { flagOffShowLoadingMessage, updateShowLoadingMessage } from '../../../../../Common/ducks/ShowLoadingMessage/ShowLoadingMessage-Actions';
import { retrieveCart } from '../RetrieveCart/RetrieveCart-Operations';
import { duplicateItem } from './DuplicateItem-Actions';

import {
    getOrderId,
} from '../../../../../Checkout/ducks/Common/Common-Selectors';
import {
    getCartOrder, getHasOrderItemDigitalProducts, getOrderItemExcludePassportAndDonation,
} from '../../Cart-Selectors';
import cartServiceClient from '../../../../../../../apis/cart-service-apis/cartServiceClient';
import { trackCartUpdate } from '../../../../../TagManager/ducks/TagManager/TagManager-Helpers';
import { getLastAddedOrderItemDetails } from '../../../../../AddToCart/AddToCart-Selectors';
import { prepareRetreiveCart } from '../RetrieveCart/PrepareRetreiveCart-Operations';
import { getFeatureFlags } from '../../../../../Checkout/ducks/App/App-Selectors';
import { processFeatureErrors } from '../../../../../Checkout/ducks/Common/ducks/Error/Error-Operations';
import { clearOrderItemErrors } from '../../../../../Checkout/ducks/Common/ducks/Error/Error-Actions';
import { callHandleCartFailure } from '../../Cart-Actions';
import { addToCartSuccess } from '../../../../../AddToCart/AddToCart-Actions';
import { getValidatedZipcode } from '../../../../../App/App-Selectors';
import { getRecipientTypeSelected } from '../../../App/App-Selectors';

const {
    auth: {
        authOperations,
    },
} = memberDucks;

const { checkJWT } = authOperations.workers;

/**
 * @description this will send the api to duplicate the item using the itemorder
 * @param {*} action
 */
function* workerDuplicateItem(action) {
    const cartId = yield select(getOrderId);
    try {
        // Show Loader
        yield put(updateShowLoadingMessage());

        // Prepare Duplicate
        const flagState = yield select(getFeatureFlags);

        const { orderItemId: cartItemId, pageProcess, history } = action.data;
        const jwtToken = yield call(checkJWT);

        yield put(clearOrderItemErrors(
            cartItemId,
            'delivery',
            { errType: '', field: 'error' }));

        const payload = {
            env: {},
            jwtToken,
            cartItemId,
            cartId,
        };

        // Call Duplicate API
        const duplicateResponse = yield call(cartServiceClient.duplicateCartItem, payload);

        // We added the prepare cart because when the location is not available
        // anymore we should get the cart after duplicate
        yield call(prepareRetreiveCart, pageProcess);
        yield call(retrieveCart);

        if (duplicateResponse?.data?.result === 'SUCCESS') {
            const recipientTypeSelected = yield select(getRecipientTypeSelected) || '';
            const cartOrderDetails = yield select(getOrderItemExcludePassportAndDonation) || '';
            const hasOrderItemDigitalProducts = yield select(getHasOrderItemDigitalProducts);

            if (
                recipientTypeSelected === 'Checked'
                && !hasOrderItemDigitalProducts
                && cartOrderDetails?.[0]?.recipientAddress?.firstName
                && ['USA', 'US', 'CAN', 'CA'].includes(cartOrderDetails?.[0]?.recipientAddress?.country)
            ) {
                const reqObj = {
                    env: {},
                    jwtToken,
                    cartId,
                    cartItemId,
                    useAddressForAll: true,
                    recipient: {
                        ...cartOrderDetails?.[0]?.recipientAddress,
                    },
                    occasions: cartOrderDetails?.[0]?.cartItems?.[0]?.occasions || null,
                    relationship: cartOrderDetails?.[0]?.relationship,
                };

                // Save Recipient Address
                yield call(cartServiceClient.saveRecipient, reqObj);

                yield call(prepareRetreiveCart, pageProcess);
                yield call(retrieveCart);
            }

            if (action?.data?.callback) {
                yield call(action.data.callback, true);
            }
        }

        let redirectToShipping = false;
        if (history && history.location?.pathname) {
            // Analize is he page is payment page or review page to forcee go to shipping
            const isMatchPath = history.location.pathname.match(/payment|review-order|review/g);
            if (isMatchPath && isMatchPath?.[0]) {
                redirectToShipping = true;
            }
        }

        if (redirectToShipping) {
            history.push(`/checkout/shipping/${cartId}`);
        }

        // Hide Loader
        yield put(flagOffShowLoadingMessage());

        const lastAddedItemInCart = yield select(getLastAddedOrderItemDetails);
        const zipCode = yield select(getValidatedZipcode);
        const clickstreamPayload = {
            eventType: 'clickstream.add-cart',
            metaData: {
                order: {
                    orderId: cartId,
                    orderItemId: lastAddedItemInCart?.cartItemId,
                },
                products: [{
                    name: lastAddedItemInCart?.product?.parentProductName,
                    price: lastAddedItemInCart?.itemInfo?.price,
                    partNumber: lastAddedItemInCart?.product?.productCode,
                    baseCode: lastAddedItemInCart?.product?.parentProductCode,
                    quantity: parseInt(lastAddedItemInCart?.itemInfo?.quantity, 10) || 1,
                }],
                zipCode: lastAddedItemInCart?.deliveryInfo?.zipCode || zipCode || '',
            },
        };

        yield put(addToCartSuccess(clickstreamPayload));

        // Retrive current cart
        // Send cart snapshot to cart track event
        const cartOrder = yield select(getCartOrder);
        yield call(trackCartUpdate, flagState, 'EventDuplicateCartItem', cartOrder);
    } catch (err) {
        mbpLogger.logError({
            cartId,
            orderItem: action.data.orderItemId,
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            function: 'duplicate item',
            jsError: err,
            message: 'duplicate item Failed',
        });
        yield put(callHandleCartFailure(err));
        yield fork(processFeatureErrors, {
            data: {
                orderItemId: cartId,
                errorFeatureType: 'delivery',
                errorValues: err.response?.data,
            },
        });
        yield put(flagOffShowLoadingMessage());
    }
}

function* watchDuplicateItem() {
    yield takeEvery(duplicateItem().type, workerDuplicateItem);
}

const watchers = [
    fork(watchDuplicateItem),
];

export { watchers };

export default {};
